import { ristretto255_oprf } from "@noble/curves/ed25519.js";
import { scryptAsync } from "@noble/hashes/scrypt.js";

// Encrypted key backup and backup PIN wraps. Everything here runs on the device. The server only
// ever gets ciphertext, a check value, the blinded PIN and a key that proves an unwrap.
//
// - A backup row is a Megolm session sealed with AES-256-GCM under the account's backup key. The
//   version, chat and session ids are the additional data, so the server cannot move a row to
//   another chat or session, or to another key, without the device noticing.
// - The keyring of backup keys is kept in the device's store sealed under a key derived from the
//   pickle key, like the rest of the device's secrets.
// - A backup PIN goes through an OPRF (RFC 9497, ristretto255-SHA512) with the server, then
//   scrypt with a random salt, and the result wraps the backup key with AES-256-GCM.

const enc = new TextEncoder();

/** A copy backed by a plain ArrayBuffer, as WebCrypto wants. */
function buf(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(bytes.length));
  out.set(bytes);
  return out;
}

export function toB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function fromB64(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
}

export function newBackupKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

async function hmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey("raw", buf(key), { name: "HMAC", hash: "SHA-256" }, false, [
    "sign",
  ]);
  return new Uint8Array(await crypto.subtle.sign("HMAC", k, buf(data)));
}

async function hkdf(key: Uint8Array, info: string): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey("raw", buf(key), "HKDF", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: enc.encode(info) },
    k,
    256,
  );
  return new Uint8Array(bits);
}

async function seal(key: Uint8Array, plain: Uint8Array, aad: string): Promise<string> {
  const k = await crypto.subtle.importKey("raw", buf(key), "AES-GCM", false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc.encode(aad) }, k, buf(plain)),
  );
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv);
  out.set(ct, iv.length);
  return toB64(out);
}

/** Opens what `seal` made. Throws when the key, the data or the additional data is wrong. */
async function open(key: Uint8Array, data: string, aad: string): Promise<Uint8Array> {
  const bytes = fromB64(data);
  const k = await crypto.subtle.importKey("raw", buf(key), "AES-GCM", false, ["decrypt"]);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: buf(bytes.subarray(0, 12)), additionalData: enc.encode(aad) },
    k,
    buf(bytes.subarray(12)),
  );
  return new Uint8Array(plain);
}

/** A value the server keeps for a backup key, so a device can test a key it was given. */
export async function keyCheck(key: Uint8Array): Promise<string> {
  return toB64(await hmac(key, enc.encode("rhema.backup.check.v1")));
}

/** What a backup row holds: a session exported at its earliest index, and whose it is. */
export type BackupPlain = {
  session_key: string;
  sender_key: string;
  sender_user: string;
  sender_device: string;
};

const rowAad = (version: string, chatId: string, sessionId: string) =>
  `rhema.backup.v1\n${version}\n${chatId}\n${sessionId}`;

export function sealSession(
  key: Uint8Array,
  version: string,
  chatId: string,
  sessionId: string,
  plain: BackupPlain,
): Promise<string> {
  return seal(key, enc.encode(JSON.stringify(plain)), rowAad(version, chatId, sessionId));
}

export async function openSession(
  key: Uint8Array,
  version: string,
  chatId: string,
  sessionId: string,
  data: string,
): Promise<BackupPlain> {
  const plain = JSON.parse(
    new TextDecoder().decode(await open(key, data, rowAad(version, chatId, sessionId))),
  ) as Partial<BackupPlain>;
  for (const field of ["session_key", "sender_key", "sender_user", "sender_device"] as const)
    if (typeof plain[field] !== "string") throw new Error("backup row is incomplete");
  return plain as BackupPlain;
}

/** Seals a device secret, such as the keyring, under a key derived from the pickle key. */
export async function sealLocal(pickleKey: Uint8Array, value: unknown): Promise<string> {
  return seal(await hkdf(pickleKey, "rhema.local.v1"), enc.encode(JSON.stringify(value)), "rhema.local");
}

export async function openLocal<T>(pickleKey: Uint8Array, data: string): Promise<T> {
  const plain = await open(await hkdf(pickleKey, "rhema.local.v1"), data, "rhema.local");
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

// Backup PIN

/** Six digits or more. */
export function validPin(pin: string): boolean {
  return /^\d{6,32}$/.test(pin);
}

const pinInput = (pin: string) => enc.encode(`rhema.backup.pin.v1\n${pin}`);

/** Blinds a PIN for the server. Only `blinded` leaves the device. */
export function blindPin(pin: string): { blind: Uint8Array; blinded: string } {
  const { blind, blinded } = ristretto255_oprf.oprf.blind(pinInput(pin));
  return { blind, blinded: toB64(blinded) };
}

/** Unblinds the server's answer into the OPRF output for this PIN. */
export function finishPin(pin: string, blind: Uint8Array, evaluated: string): Uint8Array {
  return ristretto255_oprf.oprf.finalize(pinInput(pin), blind, fromB64(evaluated));
}

/** scrypt's cost. Tests may lower it; nothing else should. */
export const pinCost = { N: 2 ** 15, r: 8, p: 1 };

export function pinWrapKey(output: Uint8Array, salt: Uint8Array): Promise<Uint8Array> {
  return scryptAsync(output, salt, { ...pinCost, dkLen: 32 });
}

const wrapAad = (version: string) => `rhema.escrow.v1\n${version}`;

export function wrapBackupKey(wrapKey: Uint8Array, version: string, key: Uint8Array): Promise<string> {
  return seal(wrapKey, key, wrapAad(version));
}

export function unwrapBackupKey(wrapKey: Uint8Array, version: string, wrapped: string): Promise<Uint8Array> {
  return open(wrapKey, wrapped, wrapAad(version));
}

/** The key the server checks unwrap proofs with. It comes from the backup key and tells nothing
 * about the PIN. */
export async function escrowAuthKey(key: Uint8Array): Promise<string> {
  return toB64(await hkdf(key, "rhema.escrow.auth.v1"));
}

export async function escrowProof(authKey: string, nonce: string): Promise<string> {
  return toB64(await hmac(fromB64(authKey), enc.encode(`rhema.escrow.proof\n${nonce}`)));
}

// Recovery through Rhema support
//
// Every backup key is also wrapped to the operator recovery key, an RSA-OAEP-4096-SHA256 key held
// in Google Cloud KMS that only the owner's account can use. The app is built with the key's
// fingerprint and refuses any other key the server might serve. Staff open the wrap with KMS only
// to issue a one-time recovery code (rhema.admin on the server), which the person enters here.

/** What went wrong with the operator recovery key. */
export class OperatorKeyError extends Error {}

function pemBody(pem: string): Uint8Array {
  const body = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s+/g, "");
  return fromB64(body);
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** SHA-256 of the key's DER SubjectPublicKeyInfo, in hex, as the app pins it. */
export async function operatorFingerprint(pem: string): Promise<string> {
  return hex(new Uint8Array(await crypto.subtle.digest("SHA-256", buf(pemBody(pem)))));
}

/** Imports the operator public key, but only when its fingerprint is the one the app pins. */
export async function importOperatorKey(pem: string, pinned: string): Promise<CryptoKey> {
  if ((await operatorFingerprint(pem)) !== pinned.toLowerCase())
    throw new OperatorKeyError("The server's recovery key is not the one this app trusts.");
  return crypto.subtle.importKey("spki", buf(pemBody(pem)), { name: "RSA-OAEP", hash: "SHA-256" }, false, [
    "encrypt",
  ]);
}

/** A backup key wrapped to the operator key. The version is inside, so the wrap cannot be moved. */
export async function wrapForOperator(
  publicKey: CryptoKey,
  version: string,
  key: Uint8Array,
): Promise<string> {
  const plain = enc.encode(`rhema.operator.v1\n${version}\n${toB64(key)}`);
  return toB64(new Uint8Array(await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, buf(plain))));
}

const CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

/** A recovery code as typed: capitals, no spaces or dashes, and 0, 1 and 8 read as O, I and B. */
export function normalizeRecoveryCode(code: string): string {
  return [...code.toUpperCase().replace(/0/g, "O").replace(/1/g, "I").replace(/8/g, "B")]
    .filter((c) => CODE_ALPHABET.includes(c))
    .join("");
}

/** Twenty characters, usually typed in four groups of five. */
export function validRecoveryCode(code: string): boolean {
  return normalizeRecoveryCode(code).length === 20;
}

/** scrypt's cost for recovery codes. The server's tool uses the same numbers. */
export const codeCost = { N: 2 ** 15, r: 8, p: 1 };

/** The key that unwraps backup keys under a recovery code, and the verifier the server checks. */
export async function recoveryCodeKeys(
  code: string,
  salt: Uint8Array,
): Promise<{ wrapKey: Uint8Array; verifier: string }> {
  const out = await scryptAsync(enc.encode(`rhema.recovery.v1\n${normalizeRecoveryCode(code)}`), salt, {
    ...codeCost,
    dkLen: 64,
  });
  return { wrapKey: out.slice(0, 32), verifier: toB64(out.slice(32)) };
}

const codeAad = (version: string) => `rhema.recovery.v1\n${version}`;

export function unwrapRecoveredKey(
  wrapKey: Uint8Array,
  version: string,
  wrapped: string,
): Promise<Uint8Array> {
  return open(wrapKey, wrapped, codeAad(version));
}

/** What the server's tool does once KMS has opened an operator wrap. Tests use it. */
export function wrapUnderCode(wrapKey: Uint8Array, version: string, key: Uint8Array): Promise<string> {
  return seal(wrapKey, key, codeAad(version));
}
